import React from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PatientDetailsCard } from "./PatientDetailsCard";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Minus } from "lucide-react";

interface Patient {
  id: string;
  patientName: string;
  emr: string;
  gender: string;
  dateOfBirth: string;
  age: number;
  phoneNumber: string;
  providerDetails: string;
  clinicDetails: string;
  primaryDx: string;
  secondaryDx: string;
  insuranceType: string;
}

interface PatientListTableProps {
  patients: Patient[];
  selectedPatients: string[];
  onPatientSelect: (patientId: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  selectCount?: number;
  onSelectCountChange?: (count: number) => void;
  filteredCount?: number;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  hideCheckboxes?: boolean;
}

export const PatientListTable: React.FC<PatientListTableProps> = ({
  patients,
  selectedPatients,
  onPatientSelect,
  onSelectAll,
  selectCount = 0,
  onSelectCountChange,
  filteredCount = 0,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  hideCheckboxes = false,
}) => {
  // Check if all patients on current page are selected
  const currentPagePatientIds = patients.map(p => p.id);
  const selectedOnCurrentPage = currentPagePatientIds.filter(id => selectedPatients.includes(id));
  const isAllSelected = patients.length > 0 && selectedOnCurrentPage.length === patients.length;
  const isIndeterminate = selectedOnCurrentPage.length > 0 && selectedOnCurrentPage.length < patients.length;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onSelectCountChange) return;
    
    const value = e.target.value;
    if (value === '') {
      onSelectCountChange(0);
      return;
    }
    
    const count = parseInt(value);
    if (isNaN(count) || count < 0) return;
    
    // Limit to filtered count
    const limitedCount = Math.min(count, filteredCount);
    if (count > filteredCount) {
      e.target.value = limitedCount.toString();
    }
    onSelectCountChange(limitedCount);
  };

  return (
    <div className="space-y-4">
      {onSelectCountChange && !hideCheckboxes && (
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-foreground">
              Select Patients
            </label>
            <Input
              type="number"
              placeholder="Enter number"
              value={selectCount || ''}
              min="1"
              max={filteredCount}
              onChange={handleInputChange}
              className="w-32 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="text-sm text-muted-foreground">
              ({selectedPatients.length} selected)
            </span>
          </div>
          <div className="text-sm text-muted-foreground">
            Filtered count - {filteredCount}
          </div>
        </div>
      )}
      {!onSelectCountChange && !hideCheckboxes && (
        <div className="flex justify-end items-center">
          <div className="text-sm text-muted-foreground">
            {selectedPatients.length} selected {filteredCount > 0 && `of ${filteredCount}`}
          </div>
        </div>
      )}

      <div id="table-admin-patient-list-container" className="border rounded-lg overflow-hidden">
        <Table id="table-admin-patient-list">
          <TableHeader>
            <TableRow id="table-admin-patient-list-header-row">
              {!hideCheckboxes && (
                <TableHead id="header-cell-select" className="w-12">
                  <div className="relative flex items-center justify-center">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={onSelectAll}
                      aria-label="Select all patients on this page"
                    />
                    {isIndeterminate && (
                      <Minus className="absolute h-3 w-3 text-primary-foreground pointer-events-none" strokeWidth={3} />
                    )}
                  </div>
                </TableHead>
              )}
              <TableHead id="header-cell-patient-details">Patient Details</TableHead>
              <TableHead id="header-cell-provider-details">Provider Details</TableHead>
              <TableHead id="header-cell-clinic-details">Clinic Details</TableHead>
              <TableHead id="header-cell-primary-dx">Primary dx</TableHead>
              <TableHead id="header-cell-secondary-dx">Secondary dx</TableHead>
              <TableHead id="header-cell-insurance-type">Insurance Type</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody id="table-admin-patient-list-body">
            {patients.map((patient) => (
              <TableRow key={patient.id} id={`row-patient-list-${patient.id}`}>
                {!hideCheckboxes && (
                  <TableCell id={`cell-patient-${patient.id}-select`}>
                    <Checkbox
                      checked={selectedPatients.includes(patient.id)}
                      onCheckedChange={(checked) => onPatientSelect(patient.id, checked as boolean)}
                      aria-label={`Select ${patient.patientName}`}
                    />
                  </TableCell>
                )}
                <TableCell id={`cell-patient-${patient.id}-details`}>
                  <PatientDetailsCard
                    patientName={patient.patientName}
                    emr={patient.emr}
                    gender={patient.gender}
                    dateOfBirth={patient.dateOfBirth}
                    age={patient.age}
                    phoneNumber={patient.phoneNumber}
                  />
                </TableCell>
                <TableCell id={`cell-patient-${patient.id}-provider`}>{patient.providerDetails}</TableCell>
                <TableCell id={`cell-patient-${patient.id}-clinic`}>{patient.clinicDetails}</TableCell>
                <TableCell id={`cell-patient-${patient.id}-primary-dx`}>{patient.primaryDx}</TableCell>
                <TableCell id={`cell-patient-${patient.id}-secondary-dx`}>{patient.secondaryDx}</TableCell>
                <TableCell id={`cell-patient-${patient.id}-insurance`}>{patient.insuranceType}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  href="#" 
                  onClick={(e) => { e.preventDefault(); currentPage > 1 && onPageChange?.(currentPage - 1); }}
                  className={currentPage <= 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>

              {(() => {
                const maxToShow = 5;
                const startPage = Math.max(1, Math.min(currentPage - Math.floor(maxToShow / 2), totalPages - maxToShow + 1));
                const endPage = Math.min(totalPages, startPage + maxToShow - 1);
                const pages = [] as number[];
                for (let p = startPage; p <= endPage; p++) pages.push(p);
                return pages.map((page) => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      href="#"
                      onClick={(e) => { e.preventDefault(); onPageChange?.(page); }}
                      className={currentPage === page ? "bg-primary text-primary-foreground" : "hover:bg-muted"}
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ));
              })()}

              <PaginationItem>
                <PaginationNext 
                  href="#" 
                  onClick={(e) => { e.preventDefault(); currentPage < totalPages && onPageChange?.(currentPage + 1); }}
                  className={currentPage >= totalPages ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};